<script setup lang="ts">
import { computed, provide, ref, toRef, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { Notify, useQuasar } from 'quasar';

import { useCommunityStore, useConsultationsStore, useConsultationTemplatesStore, useUsersStore } from '@/stores';
import type { ConsultationDto, Locales } from '@/services/types/api.types';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { useRouteNumberParam } from '@/composables/use-route-param';
import { getLanguageEnabled, getTranslation } from '@/composables/data-display-utils';
import { useSectionBreadcrumbOptions } from '@/composables/breadcrumb-options';
import { Constants } from '@/constants';

import TopPageNavigation from '@/components/TopPageNavigation.vue';
import ConsultationNavigationTabs from '@/components/ConsultationNavigationTabs.vue';
import ConsultationCreateTab from '@/components/ConsultationTabs/ConsultationCreateTab.vue';
import ConsultationEngageTab from '@/components/ConsultationTabs/ConsultationEngageTab.vue';
import ConsultationAnalyzeTab from '@/components/ConsultationTabs/ConsultationAnalyzeTab.vue';
import type { BreadcrumbElement } from '@/helpers/breadcrumbs.helper';

// PROPS
const props = defineProps<{ isTemplate: boolean }>();

// STORE
const communityStore = useCommunityStore();
const usersStore = useUsersStore();
const standardConsultationsStore = useConsultationsStore();
const consultationsStore = props.isTemplate ? useConsultationTemplatesStore() : useConsultationsStore();
const { community } = storeToRefs(communityStore);
const { currentUser } = storeToRefs(usersStore);

// CONST
const { t } = useI18n();
const $q = useQuasar();
const consultationId = useRouteNumberParam('id');
const languageEnabled = ref<Locales[]>([]);

// REFS
const activeTab = ref<string>('create');
const isDeletingMyAnswers = ref(false);
const createTabRef = ref<InstanceType<typeof ConsultationCreateTab> | null>(null);
const navigationTabsRef = ref<InstanceType<typeof ConsultationNavigationTabs> | null>(null);
const consultation = useWatchItemLoading<ConsultationDto>(consultationId, consultationsStore, (item) => {
  languageEnabled.value = getLanguageEnabled(item);
});

const isTemplate = toRef(props, 'isTemplate');

const { sectionOptions } = useSectionBreadcrumbOptions(
  computed(() => consultation.value?.id),
  isTemplate,
);

// LIFECYCLE
provide(
  'useDemographics',
  computed(() => community.value?.options.use_demographics),
);

watch(
  consultationId,
  async (id) => {
    if (id < 0) return;
    await consultationsStore.fetchConsultation(id);
  },
  { immediate: true },
);

// COMPUTED
const breadcrumbs = computed(() => {
  const breadcrumbItems: BreadcrumbElement[] = [
    {
      label: consultation.value?.title,
      to: {
        name: props.isTemplate ? 'consultation_template_details' : 'consultation_details',
        params: { id: consultation.value?.id },
      },
      icon: props.isTemplate ? 'o_settings' : 'o_folder_copy',
    },
  ];
  if (consultation.value?.id) {
    breadcrumbItems.push({
      label: t('breadcrumbs.sections'),
      to: undefined,
      icon: 'o_folder',
      options: sectionOptions.value,
    });
  }
  return breadcrumbItems;
});

const canDeleteMyAnswers = computed(() => {
  if (props.isTemplate) return false;
  if (!currentUser.value) return false;

  return (
    currentUser.value.isSuperadmin === true ||
    [Constants.User.Role.ADMIN, Constants.User.Role.EDITOR].includes(currentUser.value.role)
  );
});

// FUNCTIONS
const preview = () => {
  const previewUrl = consultationsStore.previewURL(consultationId.value);
  if (previewUrl) {
    window.open(previewUrl, '_blank')?.focus();
  }
};

const handleTabChange = (tabId: string) => {
  if (activeTab.value === 'create') {
    createTabRef.value?.executeNavigationGuard({
      onOk: () => (activeTab.value = tabId),
      onCancel: () => navigationTabsRef.value?.setActiveTab('create'),
    });
  } else {
    activeTab.value = tabId;
  }
};

const deleteMyAnswers = () => {
  $q.dialog({
    title: t('consultation.actions.deleteMyAnswers.title'),
    message: t('consultation.actions.deleteMyAnswers.message'),
    ok: {
      label: t('consultation.actions.deleteMyAnswers.confirm'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.cancel'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(async () => {
    if (props.isTemplate) return;

    isDeletingMyAnswers.value = true;
    try {
      const deleted = await standardConsultationsStore.resetMyVotes(consultationId.value);
      if (!deleted) {
        Notify.create({
          color: 'negative',
          message: t('global.notifications.errorSaving'),
          position: 'top',
          timeout: 3000,
        });
        return;
      }

      Notify.create({
        color: 'positive',
        message: t('consultation.actions.deleteMyAnswers.success', deleted),
        position: 'top',
        timeout: 4000,
      });
    } catch (error) {
      console.error(error);
      Notify.create({
        color: 'negative',
        message: t('global.notifications.errorSaving'),
        position: 'top',
        timeout: 3000,
      });
    } finally {
      isDeletingMyAnswers.value = false;
    }
  });
};
</script>
<template>
  <q-page class="consultation-detail">
    <TopPageNavigation
      :breadcrumbs="breadcrumbs"
      :language-enabled="languageEnabled"
      to="/consultations"
      @preview="preview"
    >
      <template #actions>
        <q-btn-dropdown
          v-if="canDeleteMyAnswers"
          :disable="isDeletingMyAnswers"
          color="secondary"
          dropdown-icon="expand_more"
          :label="t('global.actions')"
          unelevated
        >
          <q-list>
            <q-item clickable v-close-popup @click="deleteMyAnswers">
              <q-item-section>{{ t('consultation.actions.deleteMyAnswers.label') }}</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </template>
      <template #top-navigation>
        <consultation-navigation-tabs
          ref="navigationTabsRef"
          v-if="activeTab"
          :active-tab="activeTab"
          :is-template="isTemplate"
          @update="handleTabChange"
        />
      </template>
    </TopPageNavigation>
    <div class="title">
      <div>
        <q-icon :name="props.isTemplate ? 'o_settings' : 'o_folder_copy'" />
        <h1>{{ getTranslation(consultation, 'title', languageEnabled) }}</h1>
      </div>
    </div>
    <div>
      <div v-if="activeTab === 'create'">
        <ConsultationCreateTab
          :language-enabled="languageEnabled"
          :is-template="isTemplate"
          :consultation-id="consultationId"
          ref="createTabRef"
        />
      </div>
      <div v-else-if="activeTab === 'engage'">
        <ConsultationEngageTab />
      </div>
      <div v-else-if="activeTab === 'analyze'">
        <ConsultationAnalyzeTab :language-enabled="languageEnabled" :consultation-id="consultationId" />
      </div>
    </div>
  </q-page>
</template>
<style lang="scss">
.consultation-detail {
  padding: 16px;

  .title {
    margin: 20px 0;
    padding: 0 10px;

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      max-width: 80%;

      h1 {
        display: block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .q-icon {
        font-size: $large-title;
      }
    }
  }
}
</style>
